// LAYI HTTP login and logout.
//
// POST /layi/auth/header carrying the user's platform token in the X-Layi-Token HEADER: the server
// looks that token up (it is the user's own api_key) and derives the account from the row it
// matches. No header selects the account — who the session belongs to is never something this
// client gets to assert. The server's header-auth callback reads headers only and never the body, so
// a body-only request comes back 401. On success the gateway sets an httpOnly access_token cookie
// (SameSite=Lax) that authenticates every later HTTP call AND the WebSocket handshake.
//
// Next to the token the login says what the client is and how the person signed in to the platform
// (X-Layi-Client / -Platform / -Auth-Method, and for an Azure sign-in the Entra object id and display
// name). These are hints, never identity: they cannot change the account, its tenant or its roles.
// What they do decide is WHICH history inside the account the session uses — an Azure sign-in with a
// valid object id gets its own person partition, everything else the shared account history. The
// object id is an unsigned header, so that partition is a courtesy between colleagues, not a
// security boundary. The Azure mailbox address is never sent.
//
// Headers travel same-origin through the /layi proxy untouched, in dev and behind nginx alike.
//
// When the proxy has no gateway secret the upstream answers 200 with an HTML splash page instead
// of JSON, so a non-JSON content-type is the "gateway not configured" signal.

import { getAuth, getToken } from '../userStoredData'

export const LAYI_BASE_PATH = '/layi'

// The platform token the user already holds (the `layers_session` cookie, set at login by both the
// password and the Azure flow). It is a live credential: same-origin request only, never logged,
// never stored.
export const LAYI_TOKEN_HEADER = 'X-Layi-Token'

// Optional: the app locale at login time. The server validates it against its own catalog and
// stores it in the user metadata, so sessions that carry no userEnv (the pop-out Chainlit UI)
// still get locale-aware replies.
export const LAYI_LOCALE_HEADER = 'X-Layi-Locale'

// Optional: the model mode (fast/normal/think) selected in the widget at login time. The server
// stores the validated value in the user metadata so the pop-out's profile selector opens on the
// same mode the widget was using.
export const LAYI_MODE_HEADER = 'X-Layi-Mode'

// Optional: the output format (text/charts/files), same mechanism — the pop-out's settings
// panel opens on the widget's last selection.
export const LAYI_OUTPUT_HEADER = 'X-Layi-Output'

// Client identification hints (same wire contract as the mobile app's layiClientHeaders.ts).
export const LAYI_CLIENT_HEADER = 'X-Layi-Client'
export const LAYI_PLATFORM_HEADER = 'X-Layi-Platform'
export const LAYI_AUTH_METHOD_HEADER = 'X-Layi-Auth-Method'
export const LAYI_AZURE_ID_HEADER = 'X-Layi-Azure-Id'
export const LAYI_AZURE_DISPLAY_NAME_HEADER = 'X-Layi-Azure-Display-Name'
export const LAYI_CLIENT = 'web'
export const LAYI_PLATFORM = 'web'
const LAYI_AUTH_METHODS = ['credentials', 'azure']
// The only object-id shape the server accepts: a canonical dashed GUID.
const LAYI_AZURE_ID_PATTERN = /^[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$/
// Longest hint value the server keeps (checked on the decoded value, like the server's cap).
const LAYI_HEADER_VALUE_MAX_LENGTH = 256

// Failures travel as translation keys, never as English text: the panel renders them with `t`.
export const LAYI_NOT_CONFIGURED_REASON = 'layi_error_not_configured'

// `encodeURIComponent` that drops the value instead of throwing (a lone UTF-16 surrogate → URIError).
const percentEncode = value => {
  try {
    return encodeURIComponent(value)
  } catch (error) {
    return null
  }
}

const asAzureObjectId = value => (LAYI_AZURE_ID_PATTERN.test(value) ? value : null)

// The header for one hint value, or nothing: blank, over the cap or rejected by `toWire` → dropped,
// never truncated and never thrown.
const headerIfPresent = ({ name, value, toWire = v => v }) => {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  if (!trimmed || trimmed.length > LAYI_HEADER_VALUE_MAX_LENGTH) return {}
  const wire = toWire(trimmed)
  return wire === null ? {} : { [name]: wire }
}

// The sign-in headers for this session's auth ({ method, oid?, name? } from userStoredData.getAuth).
// Display name percent-encoded so the value on the wire is printable ASCII; the server decodes it.
export const buildClientHeaders = auth => {
  const method = auth && LAYI_AUTH_METHODS.includes(auth.method) ? auth.method : null
  return {
    [LAYI_CLIENT_HEADER]: LAYI_CLIENT,
    [LAYI_PLATFORM_HEADER]: LAYI_PLATFORM,
    ...(method ? { [LAYI_AUTH_METHOD_HEADER]: method } : {}),
    ...(method === 'azure'
      ? {
          ...headerIfPresent({ name: LAYI_AZURE_ID_HEADER, value: auth.oid, toWire: asAzureObjectId }),
          ...headerIfPresent({ name: LAYI_AZURE_DISPLAY_NAME_HEADER, value: auth.name, toWire: percentEncode })
        }
      : {})
  }
}

export const buildLoginRequest = ({ token, locale, mode, output, auth }) => ({
  method: 'POST',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    [LAYI_TOKEN_HEADER]: String(token),
    ...(locale ? { [LAYI_LOCALE_HEADER]: String(locale) } : {}),
    ...(mode ? { [LAYI_MODE_HEADER]: String(mode) } : {}),
    ...(output ? { [LAYI_OUTPUT_HEADER]: String(output) } : {}),
    ...buildClientHeaders(auth)
  },
  // The server reads headers only; an empty body keeps the request a well-formed POST.
  body: '{}'
})

export const loginToLayi = async ({ locale, mode, output, token = getToken(), auth = getAuth() } = {}) => {
  // No token means no logged-in platform session, so there is nothing to prove.
  if (!token) return { ok: false, reason: 'layi_error_no_user' }

  let response
  try {
    response = await fetch(`${LAYI_BASE_PATH}/auth/header`, buildLoginRequest({ token, locale, mode, output, auth }))
  } catch (error) {
    console.warn('LAYI login request failed', error)
    return { ok: false, reason: 'layi_error_unreachable' }
  }

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) return { ok: false, reason: LAYI_NOT_CONFIGURED_REASON }

  if (!response.ok) {
    console.warn(`LAYI login failed (${response.status})`)
    return { ok: false, reason: 'layi_error_login_failed' }
  }

  try {
    await response.json()
  } catch (error) {
    return { ok: false, reason: LAYI_NOT_CONFIGURED_REASON }
  }

  return { ok: true, reason: null }
}

export const buildLogoutRequest = () => ({ method: 'POST', credentials: 'include', keepalive: true })

// POST /layi/logout clears the LAYI access_token cookie, which would otherwise outlive the platform
// session. Best effort and fire-and-forget: the platform logout never waits for it or fails with it,
// and `keepalive` lets the request finish while the Entra logout redirect navigates away. It clears
// the cookie only — the JWT itself is stateless and expires on its own (1 day on the external LAYI).
export const logoutFromLayi = () => {
  try {
    const pending = fetch(`${LAYI_BASE_PATH}/logout`, buildLogoutRequest())
    if (pending && typeof pending.catch === 'function') pending.catch(() => {})
  } catch (error) {
    // fetch itself unavailable or threw synchronously: nothing to clean up server-side from here.
  }
}
